import { useRef, useState } from 'react'

export function formatUTCDate(value: string | Date | null): string {
	if (!value) return '—'
	const d = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(d.getTime())) return '—'
	const dd = String(d.getUTCDate()).padStart(2, '0')
	const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
	const yyyy = d.getUTCFullYear()
	return `${mm}-${dd}-${yyyy}`
}

export function formatHireDate(isoDate: string | null): string {
	const formatted = formatUTCDate(isoDate)
	if (formatted === '—') return formatted
	const hire = new Date(isoDate as string)
	const now = new Date()
	const totalDays = Math.floor((now.getTime() - hire.getTime()) / (1000 * 60 * 60 * 24))
	if (totalDays < 0) return formatted
	const years = Math.floor(totalDays / 365.25)
	const remainingDays = Math.max(0, totalDays - Math.floor(years * 365.25))
	return `${formatted} (${years}y ${remainingDays}d)`
}

export function useTrendTooltipPos() {
	const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | undefined>()
	const chartWrapperRef = useRef<HTMLDivElement>(null)

	const onMouseMove = (state: { activeCoordinate?: { x: number; y: number } | null }) => {
		if (state.activeCoordinate != null) {
			const containerWidth = chartWrapperRef.current?.offsetWidth ?? 600
			const x = state.activeCoordinate.x
			const flipLeft = x > containerWidth * 0.65
			setTooltipPos({ x: flipLeft ? x - 200 : x + 16, y: state.activeCoordinate.y - 40 })
		}
	}

	return { tooltipPos, chartWrapperRef, onMouseMove, onMouseLeave: () => setTooltipPos(undefined) }
}

export function trendLegendFormatter(value: string) {
	return <span style={{ color: '#6B7280' }}>{value}</span>
}

export function getTrendYAxisProps(scaleMax = 10) {
	return {
		domain: [0, scaleMax] as [number, number],
		ticks: Array.from({ length: 6 }, (_, i) => (i * scaleMax) / 5),
		tick: { fontSize: 12, fill: '#9CA3AF' },
		axisLine: false,
		tickLine: false,
	}
}

export const TREND_CURSOR = { stroke: '#CBD5E1', strokeWidth: 1, strokeDasharray: '4 4' }

export function StatusBadge({ status }: Readonly<{ status: string }>) {
	const colors: Record<string, string> = {
		'in-progress': 'bg-blue-100 text-blue-800',
		completed: 'bg-green-100 text-green-800',
		draft: 'bg-neutral-100 text-neutral-800',
		'ready-for-launch': 'bg-yellow-100 text-yellow-800',
	}

	return (
		<span
			className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] || 'bg-neutral-100 text-neutral-800'}`}>
			{status}
		</span>
	)
}

export function formatDate(value: string | Date): string {
	const d = value instanceof Date ? value : new Date(value)
	return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

export function formatDealsInvolved(count: number): string {
	if (count === 0) return 'No Deals'
	if (count === 1) return '1 Deal'
	return `${count} Deals`
}

export const DISTRIBUTION_TIERS = [
	{ key: 'critical' as const, label: 'Critical', color: '#FCA5A5', textColor: '#DC2626' },
	{ key: 'needsImprovement' as const, label: 'Needs Improvement', color: '#FCC4C4', textColor: '#DC2626' },
	{ key: 'average' as const, label: 'Average', color: '#FFCF99', textColor: '#D97706' },
	{ key: 'good' as const, label: 'Good', color: '#BAD6FD', textColor: '#2563EB' },
	{ key: 'excellent' as const, label: 'Excellent', color: '#A7F3D0', textColor: '#059669' },
]

export type DistributionCounts = {
	critical: number
	needsImprovement: number
	average: number
	good: number
	excellent: number
	total: number
}

export function getRatingColor(value: number, scaleMax = 10): string {
	const normalized = scaleMax === 10 ? value : (value * 10) / scaleMax
	if (normalized >= 8) return 'bg-emerald-100 text-emerald-600'
	if (normalized >= 6) return 'bg-blue-100 text-blue-600'
	if (normalized >= 4) return 'bg-orange-100 text-orange-600'
	return 'bg-red-100 text-red-600'
}

export function RatingChip({
	value,
	scaleMax = 10,
	className = '',
}: Readonly<{
	value: number | null
	scaleMax?: number
	className?: string
}>) {
	if (value === null) return <span className='text-sm text-neutral-400'>—</span>
	return (
		<span
			className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getRatingColor(value, scaleMax)} ${className}`}>
			{value.toFixed(2)}
		</span>
	)
}

export function VsOverall({ value }: Readonly<{ value: number | null }>) {
	if (value === null) return <span className='text-sm text-neutral-400'>—</span>
	const sign = value > 0 ? '+' : ''
	let className = 'text-neutral-600'
	if (value > 0) className = 'text-emerald-600'
	else if (value < 0) className = 'text-red-600'
	return (
		<span className={`text-sm font-semibold ${className}`}>
			{sign}
			{value.toFixed(2)}
		</span>
	)
}

type TrendTooltipEntry = { name: string; value: number; color: string }

export function TrendChartTooltip({
	active,
	payload,
	label,
	seriesOrder,
}: Readonly<{
	active?: boolean
	payload?: TrendTooltipEntry[]
	label?: string
	seriesOrder: string[]
}>) {
	if (!active || !payload?.length) return null
	return (
		<div className='rounded-lg border border-neutral-200 bg-white px-3 py-2 shadow-md text-sm'>
			<p className='mb-1 text-xs font-medium text-neutral-500'>{label}</p>
			{payload
				.filter((e) => typeof e.color === 'string' && !e.color.startsWith('url('))
				.sort((a, b) => seriesOrder.indexOf(a.name) - seriesOrder.indexOf(b.name))
				.map((entry) => (
					<div key={entry.name} className='flex items-center gap-2'>
						<span className='h-2 w-2 rounded-full' style={{ backgroundColor: entry.color }} />
						<span className='text-neutral-600'>{entry.name}:</span>
						<span className='font-semibold' style={{ color: entry.color }}>
							{typeof entry.value === 'number' ? entry.value.toFixed(2) : '—'}
						</span>
					</div>
				))}
		</div>
	)
}

export function DistributionBar({ distribution }: Readonly<{ distribution: DistributionCounts }>) {
	const [tooltip, setTooltip] = useState<{
		label: string
		pct: number
		count: number
		x: number
		color: string
		textColor: string
	} | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	const rawSegments = DISTRIBUTION_TIERS.map((tier) => ({
		...tier,
		count: distribution[tier.key],
		pct: distribution.total > 0 ? (distribution[tier.key] / distribution.total) * 100 : 0,
	})).filter((s) => s.count > 0)
	const roundedSum = rawSegments.slice(0, -1).reduce((sum, s) => sum + Math.round(s.pct), 0)
	const segments = rawSegments.map((s, i) => ({
		...s,
		pct: i < rawSegments.length - 1 ? Math.round(s.pct) : 100 - roundedSum,
	}))

	if (segments.length === 0) {
		return <div className='h-6 w-full rounded bg-neutral-100' />
	}

	return (
		<div ref={containerRef} className='relative'>
			<button
				type='button'
				aria-label='Rating distribution'
				className='flex h-6 w-full overflow-hidden rounded p-0 border-0 bg-transparent'
				onClick={() => setTooltip(null)}
				onMouseMove={(e) => {
					const rect = e.currentTarget.getBoundingClientRect()
					const x = e.clientX - rect.left
					const pct = (x / rect.width) * 100
					let cumulative = 0
					for (const seg of segments) {
						cumulative += seg.pct
						if (pct <= cumulative) {
							setTooltip({
								label: seg.label,
								pct: seg.pct,
								count: seg.count,
								x,
								color: seg.color,
								textColor: seg.textColor,
							})
							return
						}
					}
				}}
				onMouseLeave={() => setTooltip(null)}>
				{segments.map((seg) => (
					<div
						key={seg.key}
						style={{
							width: `${seg.pct}%`,
							backgroundColor: seg.color,
							minWidth: '2px',
							opacity: tooltip !== null && tooltip.label !== seg.label ? 0.3 : 1,
							transition: 'opacity 150ms',
						}}
						aria-hidden='true'
					/>
				))}
			</button>
			{tooltip && (
				<div
					className='absolute -top-10 z-10 -translate-x-1/2 flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 shadow-sm pointer-events-none'
					style={{ left: tooltip.x }}>
					<span
						className='rounded-full px-2 py-0.5 text-xs font-semibold'
						style={{ backgroundColor: tooltip.color, color: tooltip.textColor }}>
						{tooltip.label}
					</span>
					<span className='text-xs text-neutral-900'>
						{tooltip.pct}% ({tooltip.count})
					</span>
				</div>
			)}
		</div>
	)
}
